import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import UserFormModal from './UserFormModal';
import PasswordModal from './PasswordModal';
import UserDetailsDrawer from './UserDetailsDrawer';
import {
  downloadCsv,
  formatDateTime,
  formatRelativeTime,
  formatShortDateTime,
  roleMeta,
  todayFilename,
} from './adminFormatters';
import './adminLayout.sass';

const ROLE_OPTIONS = [
  { value: 'all', label: 'Все роли' },
  { value: 'admin', label: 'Админы' },
  { value: 'manager', label: 'Руководители' },
  { value: 'user', label: 'Пользователи' },
];

const ACTIVE_OPTIONS = [
  { value: 'all', label: 'Все статусы' },
  { value: 'active', label: 'Активные' },
  { value: 'blocked', label: 'Заблокированные' },
];

const AdminUsers = () => {
  const { listUsers, createUser, updateUser, setPassword, deleteUser, getUser, onlineUserIds } =
    useAdminSocketContext();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editUser, setEditUser] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdUser, setPwdUser] = useState(null);
  const [drawerUserId, setDrawerUserId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listUsers({ search, limit: 200 });
      setUsers(res.users || []);
      setTotal(res.total || 0);
      setLastLoadedAt(new Date().toISOString());
    } catch (e) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [listUsers, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const onlineSet = useMemo(
    () => new Set((onlineUserIds || []).map(Number)),
    [onlineUserIds]
  );

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        if (roleFilter !== 'all' && user.role !== roleFilter) return false;
        if (activeFilter === 'active') return user.is_active !== false;
        if (activeFilter === 'blocked') return user.is_active === false;
        return true;
      }),
    [users, roleFilter, activeFilter]
  );

  const filtersActive = Boolean(searchInput) || roleFilter !== 'all' || activeFilter !== 'all';

  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setRoleFilter('all');
    setActiveFilter('all');
  };

  const handleCreate = () => {
    setFormMode('create');
    setEditUser(null);
    setFormOpen(true);
  };

  const handleEdit = (record) => {
    setFormMode('edit');
    setEditUser(record);
    setFormOpen(true);
  };

  const handleFormSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      if (formMode === 'create') {
        await createUser({
          username: values.username,
          password: values.password,
          role: values.role,
          is_active: values.is_active,
          profile: {
            displayName: values.displayName,
            bio: values.bio,
            location: values.location,
            gender: values.gender,
          },
        });
        message.success('Пользователь создан');
      } else {
        await updateUser({
          userId: editUser.id,
          username: values.username,
          role: values.role,
          is_active: values.is_active,
          displayName: values.displayName,
          bio: values.bio,
          location: values.location,
          gender: values.gender,
        });
        message.success('Пользователь обновлён');
      }
      setFormOpen(false);
      load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePassword = async (password) => {
    setSubmitLoading(true);
    try {
      await setPassword({ userId: pwdUser.id, password });
      message.success('Пароль изменён');
      setPwdOpen(false);
    } catch (e) {
      message.error(e.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleActivate = async (record) => {
    try {
      await updateUser({
        userId: record.id,
        is_active: true,
      });
      message.success('Пользователь активирован');
      load();
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleDeactivate = async (record) => {
    try {
      await deleteUser({ userId: record.id, hard: false });
      message.success('Пользователь заблокирован');
      load();
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleExport = () => {
    downloadCsv(
      todayFilename('users'),
      [
        { title: 'ID', dataIndex: 'id' },
        { title: 'Логин', dataIndex: 'username' },
        { title: 'Роль', value: (r) => roleMeta(r.role).label },
        { title: 'Активен', value: (r) => (r.is_active !== false ? 'Да' : 'Нет') },
        { title: 'Последний вход', value: (r) => formatDateTime(r.last_login_at) },
        { title: 'Задач', dataIndex: 'tasks_count' },
        { title: 'Запросов', dataIndex: 'requests_count' },
      ],
      filteredUsers
    );
    message.success(`Экспортировано пользователей: ${filteredUsers.length}`);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 64, sorter: (a, b) => a.id - b.id },
    {
      title: 'Логин',
      dataIndex: 'username',
      sorter: (a, b) => a.username.localeCompare(b.username, 'ru'),
      render: (username, record) => (
        <span className="admin-users__name">
          <span
            className={`admin-users__dot${onlineSet.has(Number(record.id)) ? ' is-online' : ''}`}
            title={onlineSet.has(Number(record.id)) ? 'Сейчас в сервисе' : 'Не в сети'}
          />
          <button
            type="button"
            className="admin-user-link"
            onClick={() => setDrawerUserId(record.id)}
          >
            {username}
          </button>
        </span>
      ),
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      width: 140,
      render: (role) => {
        const meta = roleMeta(role);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Доступ',
      dataIndex: 'is_active',
      width: 120,
      render: (value) => (
        <Tag color={value !== false ? 'success' : 'warning'}>
          {value !== false ? 'Активен' : 'Заблокирован'}
        </Tag>
      ),
    },
    {
      title: 'Последний вход',
      dataIndex: 'last_login_at',
      width: 150,
      sorter: (a, b) => new Date(a.last_login_at || 0) - new Date(b.last_login_at || 0),
      render: (value) => (
        <Tooltip title={formatDateTime(value)}>
          <span>{formatRelativeTime(value)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Задач',
      dataIndex: 'tasks_count',
      width: 80,
      align: 'right',
      sorter: (a, b) => (a.tasks_count || 0) - (b.tasks_count || 0),
    },
    {
      title: 'Запросов',
      dataIndex: 'requests_count',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.requests_count || 0) - (b.requests_count || 0),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => setDrawerUserId(record.id)}>
            Просмотр
          </Button>
          <Button size="small" onClick={() => handleEdit(record)}>
            Изменить
          </Button>
          <Button
            size="small"
            onClick={() => {
              setPwdUser(record);
              setPwdOpen(true);
            }}
          >
            Пароль
          </Button>
          {record.is_active !== false && (
            <Popconfirm title="Заблокировать?" onConfirm={() => handleDeactivate(record)}>
              <Button size="small" danger>
                Блок
              </Button>
            </Popconfirm>
          )}
          {record.is_active === false && (
            <Popconfirm title="Активировать пользователя?" onConfirm={() => handleActivate(record)}>
              <Button size="small" type="primary">
                Активировать
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="admin-tab-toolbar">
        <Space wrap>
          <Input.Search
            placeholder="Поиск по логину"
            allowClear
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(value) => setSearch(value.trim())}
            style={{ width: 220 }}
          />
          <Select
            value={roleFilter}
            onChange={setRoleFilter}
            options={ROLE_OPTIONS}
            style={{ width: 160 }}
          />
          <Select
            value={activeFilter}
            onChange={setActiveFilter}
            options={ACTIVE_OPTIONS}
            style={{ width: 170 }}
          />
          {filtersActive && (
            <Button type="link" onClick={resetFilters}>
              Сбросить
            </Button>
          )}
        </Space>
        <Space wrap>
          <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!filteredUsers.length}>
            CSV
          </Button>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Обновить
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Создать
          </Button>
        </Space>
      </div>

      <Space size={12} wrap className="admin-list-meta">
        <Badge
          color="blue"
          text={`Показано: ${filteredUsers.length} из ${
            roleFilter !== 'all' || activeFilter !== 'all' ? users.length : total
          }`}
        />
        {lastLoadedAt && (
          <span className="admin-list-meta__muted">
            Обновлено: {formatShortDateTime(lastLoadedAt)}
          </span>
        )}
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredUsers}
        columns={columns}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (count) => `Пользователей: ${count}`,
        }}
        scroll={{ x: 1100 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={filtersActive ? 'Ничего не найдено — измените фильтры' : 'Пользователей пока нет'}
            />
          ),
        }}
      />

      <UserFormModal
        open={formOpen}
        mode={formMode}
        initialValues={
          editUser
            ? {
                username: editUser.username,
                role: editUser.role,
                is_active: editUser.is_active !== false,
                displayName: editUser.display_name,
                bio: editUser.bio,
                location: editUser.location,
                gender: editUser.gender,
              }
            : undefined
        }
        onCancel={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        loading={submitLoading}
      />

      <PasswordModal
        open={pwdOpen}
        username={pwdUser?.username}
        onCancel={() => setPwdOpen(false)}
        onSubmit={handlePassword}
        loading={submitLoading}
      />

      <UserDetailsDrawer
        open={!!drawerUserId}
        userId={drawerUserId}
        onClose={() => setDrawerUserId(null)}
        getUser={getUser}
        isOnline={(onlineUserIds || []).map(Number).includes(Number(drawerUserId))}
      />
    </>
  );
};

export default AdminUsers;
